import { createContext, useState, useEffect, use, useCallback, useRef } from "react"
import { DomainEventDetail, DomainEvents } from "../../domain/_kernel/Events"
import { Toaster, ToasterHandle, ToastProps } from "@cividao/components"
import { useTranslation } from "react-i18next"
import { TransactionStatus } from "../../domain/ethereum/Models/Transaction"
import { ErrorCodes } from "../../domain/_kernel/ErrorCodes"
import { useLocalStorage } from "../../hooks/useLocalStorage"
import { LayoutContextInterface, LayoutProviderProps, THEME_MODE_STORAGE_KEY, ThemeMode } from "./types"
import { ErrorModal } from "../../components/modals/ErrorModal"
import { ErrorModalProps } from "../../components/modals/ErrorModal/types"

const initialState: LayoutContextInterface = {
  themeMode: ThemeMode.LIGHT,
  setThemeMode: () => {},
  addToast: () => {},
  removeToast: () => {},
  clearAllToasts: () => {},
}

const Context = createContext<LayoutContextInterface>(initialState)

export const LayoutContext = ({ children }: LayoutProviderProps) => {
  const [themeMode, setThemeMode] = useLocalStorage<ThemeMode>(THEME_MODE_STORAGE_KEY, ThemeMode.LIGHT)
  // Modals
  const [errorModal, setErrorModal] = useState<ErrorModalProps | null>(null)
  const openErrorModal = (modal: ErrorModalProps) => setErrorModal(modal)
  const closeErrorModal = () => setErrorModal(null)

  const toasterRef = useRef<ToasterHandle>(null)
  const { t } = useTranslation("common")

  const addToast = useCallback((toast: Omit<ToastProps, "id" | "isExiting" | "onClose"> & { duration?: number }) =>toasterRef.current?.addToast({ ...toast, onClose: () => {} }),[]) // prettier-ignore
  const removeToast = useCallback((id: string) => toasterRef.current?.removeToast(id), [])
  const clearAllToasts = useCallback(() => toasterRef.current?.clearAllToasts(), [])

  const handleFinishedTransaction = useCallback(
    (e: CustomEvent<DomainEventDetail[DomainEvents.SUCCESS_TRANSACTION | DomainEvents.REVERTED_TRANSACTION]>) => {
      const { type, status } = e.detail.transaction.serialize()

      addToast({
        message: t(`transactions.types.${type}`, type),
        variant: status === TransactionStatus.SUCCESS ? "success" : "error",
        duration: 10000,
      })
    },
    [addToast, t],
  )

  const handleDomainError = useCallback((e: CustomEvent<DomainEventDetail[DomainEvents.ERROR]>) => {
    switch (e.detail.domainError.code) {
      case ErrorCodes.SIGNATURE_ERROR:
      case ErrorCodes.TRANSACTION_ERROR:
        openErrorModal({ error: e.detail.domainError, onClose: closeErrorModal })
        break
    }
  }, [])

  // Apply theme to html element
  useEffect(() => {
    const htmlElement = document.documentElement
    if (htmlElement) {
      htmlElement.classList.remove(ThemeMode.LIGHT, ThemeMode.DARK)
      htmlElement.classList.add(themeMode)
    }
  }, [themeMode])

  useEffect(() => {
    const finishedTransactionListener = handleFinishedTransaction as EventListener
    const domainErrorListener = handleDomainError as EventListener

    window.addEventListener(DomainEvents.SUCCESS_TRANSACTION, finishedTransactionListener)
    window.addEventListener(DomainEvents.REVERTED_TRANSACTION, finishedTransactionListener)
    window.addEventListener(DomainEvents.ERROR, domainErrorListener)

    return () => {
      window.removeEventListener(DomainEvents.SUCCESS_TRANSACTION, finishedTransactionListener)
      window.removeEventListener(DomainEvents.REVERTED_TRANSACTION, finishedTransactionListener)
      window.removeEventListener(DomainEvents.ERROR, domainErrorListener)
    }
  }, [handleDomainError, handleFinishedTransaction])

  return (
    <Context.Provider
      value={{
        themeMode,
        setThemeMode,
        addToast,
        removeToast,
        clearAllToasts,
      }}
    >
      {errorModal && <ErrorModal {...errorModal} />}
      <Toaster ref={toasterRef} />
      {children}
    </Context.Provider>
  )
}

export const useLayout = function () {
  const context = use(Context)
  if (context === undefined) {
    throw new Error(`useLayout must be used within a LayoutContextProvider`)
  }
  return context
}

export { type LayoutContextInterface, type LayoutProviderProps, ThemeMode }
